import { useEffect } from "react";

const SITE_URL = "https://shillongmorningteerresulttoday.com";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const DEFAULT_IMAGE_ALT = "Shillong Morning Teer Result Today – Live FR and SR Results";

const setMeta = (attr, key, content) => {
    if (content === undefined || content === null) return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
};

const setLink = (rel, href) => {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
    let el = document.head.querySelector(`script[data-seo-id="${id}"]`);
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.setAttribute("data-seo-id", id);
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
};

/**
 * useSeo — sets title, description, keywords, canonical, OG/Twitter and breadcrumb JSON-LD per route.
 * Options:
 *   - title, description, keywords, path
 *   - ogTitle / ogDescription (fall back to title / description)
 *   - image, imageAlt (absolute HTTPS URL preferred)
 *   - breadcrumbs: [{ name, path }]
 *   - jsonLd: extra JSON-LD block
 */
export const useSeo = ({
    title,
    description,
    keywords,
    path = "/",
    ogTitle,
    ogDescription,
    image = DEFAULT_IMAGE,
    imageAlt = DEFAULT_IMAGE_ALT,
    breadcrumbs = [],
    jsonLd = null,
}) => {
    const bcKey = JSON.stringify(breadcrumbs);
    const jlKey = JSON.stringify(jsonLd);
    useEffect(() => {
        const url = `${SITE_URL}${path}`;
        const finalOgTitle = ogTitle || title;
        const finalOgDesc = ogDescription || description;

        if (title) document.title = title;

        setMeta("name", "description", description);
        if (keywords) setMeta("name", "keywords", keywords);

        setLink("canonical", url);

        // Open Graph
        setMeta("property", "og:type", "website");
        setMeta("property", "og:site_name", "Shillong Morning Teer Result Today");
        setMeta("property", "og:title", finalOgTitle);
        setMeta("property", "og:description", finalOgDesc);
        setMeta("property", "og:url", url);
        setMeta("property", "og:image", image);
        setMeta("property", "og:image:secure_url", image);
        setMeta("property", "og:image:type", "image/png");
        setMeta("property", "og:image:width", "1200");
        setMeta("property", "og:image:height", "630");
        setMeta("property", "og:image:alt", imageAlt);
        setMeta("property", "og:locale", "en_IN");

        // Twitter
        setMeta("name", "twitter:card", "summary_large_image");
        setMeta("name", "twitter:title", finalOgTitle);
        setMeta("name", "twitter:description", finalOgDesc);
        setMeta("name", "twitter:image", image);
        setMeta("name", "twitter:image:alt", imageAlt);
        setMeta("name", "twitter:url", url);
        setMeta("name", "twitter:domain", "shillongmorningteerresulttoday.com");

        if (breadcrumbs && breadcrumbs.length) {
            setJsonLd("breadcrumb", {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: breadcrumbs.map((b, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    name: b.name,
                    item: `${SITE_URL}${b.path}`,
                })),
            });
        }

        if (jsonLd) setJsonLd("page", jsonLd);
    }, [title, description, keywords, path, ogTitle, ogDescription, image, imageAlt, bcKey, jlKey]);
};
